import config from 'config';

import { FC, Fragment } from 'react';
import moment from 'moment';
// Hooks
import useMenu from 'hooks/useMenu';
import useToggle from 'hooks/useToggle';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { deleteQuiz, cloneQuiz, generateRichMedia, startQuiz, endQuiz } from 'store/quiz/quizzesAsync';
import { selectLoading, selectIsStartNowVisible, selectIsEndNowVisible } from 'store/quiz/quizzesSelectors';
import { selectCurrentUser, selectEventsFeatureEnable } from 'store/currentUser/currentUserSelectors';
import { RootState } from 'store';
import IQuiz from 'models/Quiz';
import QuizTypes from 'types/QuizTypes';
import UserRoles from 'types/UserRoles';
import AccountFeatures from 'types/AccountFeatures';
// Icons
import { FaEllipsisV, FaChevronDown } from 'react-icons/fa';
// Component
import { IconButton } from 'components/Buttons';
import { Tooltip } from 'components/Tooltip';
import ConfirmationDialog from 'dialogs/Confirmation.dialog'
import QuizGeofenceFormDialog from 'dialogs/QuizGeofenceForm.dialog';
import QRCodeGeneratorDialog from 'dialogs/QRCodeGenerator.dialog';
import ShareQuizLinkDialog from 'dialogs/ShareQuizLink.dialog';
import QuizAgentDialog from 'dialogs/QuizAgentDialog';
// i18next
import { useTranslation } from 'react-i18next';

type Props = {
  quiz: IQuiz;
  isInternal?: boolean;
}

const QuizMenu:FC<Props> = ({ quiz, isInternal = false }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const currentUser = useAppSelector(selectCurrentUser);
  const loading:boolean = useAppSelector(selectLoading);
  const eventsFeatureEnable = useAppSelector(selectEventsFeatureEnable);
  const isStartNowVisible:boolean = useAppSelector((state:RootState) => selectIsStartNowVisible(state, { quizId: quiz._id }));
  const isEndNowVisible:boolean = useAppSelector((state:RootState) => selectIsEndNowVisible(state, { quizId: quiz._id }));

  const { open:openConfirmationDialog, toggle:toggleConfirmationDialog } = useToggle();
  const { open:openQuizGeofenceFormDialog, toggle:toggleQuizGeofenceFormDialog } = useToggle();
  const { open:openQRCodeGeneratorDialog, toggle:toggleQRCodeGeneratorDialog } = useToggle();
  const { open:openShareQuizLinkDialog, toggle:toggleShareQuizLinkDialog } = useToggle();
  const { open:openQuizAgentDialog, toggle:toggleQuizAgentDialog } = useToggle();

  const isPollQuizzes = quiz && [QuizTypes.Poll, QuizTypes.RankingPoll].includes(quiz.type);
  const isTeamSelectorQuiz = quiz && quiz.type === QuizTypes.TeamSelector;

  const handleCloneQuiz = () => {
    dispatch(cloneQuiz({
      id: quiz._id, quizName: `${quiz.quizName} - clone - ${moment().format('YYYYMMDDhhmmss')}`
    }));
  }

  const handleStartQuiz = () => {
    dispatch(startQuiz(quiz._id));
  }

  const handleEndQuiz = () => {
    dispatch(endQuiz(quiz._id));
  }

  const handleLiveLeaderBoard = () => {
    const endpoint = isPollQuizzes ? 'live-results' : isTeamSelectorQuiz ? 'live-teamboard' : 'live-leaderboard';
    window.open(`${config.quizURL}/quiz/${quiz.quizNameSlug}/${endpoint}`);
  };

  const handleGenerateRichMedia = () => {
    dispatch(generateRichMedia(quiz._id));
  }

  const handleConfirm = async () => {
    try {
      await dispatch(deleteQuiz(quiz._id)).unwrap();
    } catch(err){}
  }

  const isAdmin = currentUser?.groups.includes(UserRoles.Admin);

  const { Menu, MenuItem, openMenu } = useMenu();

  const disabledQuizAgent = [QuizTypes.BeatTheExpert, QuizTypes.Streak, QuizTypes.TeamSelector].includes(quiz.type);

  return (
    <Fragment>
      <div className="relative inline-block">
        <Tooltip className="ml-1" title={isInternal ? t('buttons.actions') : t('buttons.more')}>
          <IconButton
            onClick={openMenu}
          >
            {isInternal ? <FaChevronDown /> : <FaEllipsisV />}
          </IconButton>
        </Tooltip>
        <Menu>
          {currentUser?.account?.features?.includes(AccountFeatures.QuizAgent) ? (
            <MenuItem onClick={toggleQuizAgentDialog} disabled={disabledQuizAgent}>
              {t('pages.quizzes.editWithQuizAgentButton')}
            </MenuItem>
          ) : null}
          {[QuizTypes.Scoring, QuizTypes.Prediction, QuizTypes.Poll, QuizTypes.RankingPoll, QuizTypes.TeamSelector, QuizTypes.BeatTheExpert, QuizTypes.Streak].includes(quiz.type) && !isInternal && (
            <MenuItem
              onClick={handleLiveLeaderBoard}
              disabled={!quiz.publicLeaderboard?.enabled}
            >
              {isPollQuizzes || isTeamSelectorQuiz ? t('components.quizMenu.liveResults') : t('components.quizMenu.liveLeaderboard')}
            </MenuItem>
          )}
          {!isAdmin && ([
            <MenuItem
              key="quizGeofence"
              onClick={toggleQuizGeofenceFormDialog}
            >{t('components.quizMenu.quizGeofence')}</MenuItem>,
            <MenuItem
              key="generateRichMedia"
              onClick={handleGenerateRichMedia}
            >{t('components.quizMenu.generateRichMedia')}</MenuItem>,
            <MenuItem
              key="generateQrCode"
              onClick={toggleQRCodeGeneratorDialog}
            >{t('components.quizMenu.generateQrCode')}</MenuItem>,
            <MenuItem
              key="shareQuizLink"
              onClick={toggleShareQuizLinkDialog}
            >{t('components.quizMenu.shareQuizLink')}</MenuItem>,
            <MenuItem
              key="analytics"
              component="a"
              {...{
                href: `/owner/quizzes/${quiz._id}/analytics`,
                target: "_blank",
                rel: "noopener noreferrer",
              }}
            >
              {t('components.quizMenu.analytics')}
            </MenuItem>,
            <MenuItem
              key="answersAnalytics"
              component="a"
              {...{
                href: `/owner/quizzes/${quiz._id}/answers-analytics`,
                target: "_blank",
                rel: "noopener noreferrer",
              }}
            >
              {t('components.quizMenu.answersAnalytics')}
            </MenuItem>,
            <MenuItem
              key="report"
              component="a"
              {...{
                href: `/owner/quizzes/${quiz._id}/report`,
                target: "_blank",
                rel: "noopener noreferrer",
              }}
            >
              {t('components.quizMenu.report')}
            </MenuItem>
          ])}
          {!isInternal ? (
            <MenuItem
              onClick={handleCloneQuiz}
            >{t('components.quizMenu.cloneQuiz')}</MenuItem>
          ) : null}
          {isStartNowVisible && !isAdmin && (
            <MenuItem
              onClick={handleStartQuiz}
            >{t('components.quizMenu.startNow')}</MenuItem>
          )}
          {isEndNowVisible && !isAdmin && (
            <MenuItem
              onClick={handleEndQuiz}
            >{t('components.quizMenu.stopNow')}</MenuItem>
          )}
          {eventsFeatureEnable ? (
            <MenuItem
              component="a"
              {...{
                href: `/owner/quizzes/${quiz._id}/quiz-events`,
                target: "_blank",
                rel: "noopener noreferrer",
              }}
            >
              {t('components.quizMenu.events')}
            </MenuItem>
          ) : null}
          {!isInternal ? (
            <MenuItem
              color="error"
              onClick={toggleConfirmationDialog}
            >{t('components.quizMenu.deleteButton')}</MenuItem>
          ) : null}
        </Menu>
      </div>
      {openConfirmationDialog ? (
        <ConfirmationDialog
          title={t('components.quizMenu.deleteTitle', { quizName: quiz.quizName })}
          content={t('components.quizMenu.deleteConfirmation')}
          open={openConfirmationDialog}
          loading={loading}
          onClose={toggleConfirmationDialog}
          onConfirm={handleConfirm}
        />
      ) : null}
      {openQuizGeofenceFormDialog ? (
        <QuizGeofenceFormDialog
          quizId={quiz._id}
          open={openQuizGeofenceFormDialog}
          onClose={toggleQuizGeofenceFormDialog}
        />
      ) : null}
      {openQRCodeGeneratorDialog ? (
        <QRCodeGeneratorDialog
          open={openQRCodeGeneratorDialog}
          onClose={toggleQRCodeGeneratorDialog}
          quizName={quiz.quizName}
          quizNameSlug={quiz.quizNameSlug}
          qrCodeLogo={quiz.logoImage}
        />
      ) : null}
      {openShareQuizLinkDialog ? (
        <ShareQuizLinkDialog
          open={openShareQuizLinkDialog}
          onClose={toggleShareQuizLinkDialog}
          quizNameSlug={quiz.quizNameSlug}
        />
      ) : null}
      {openQuizAgentDialog ? (
        <QuizAgentDialog
          open={openQuizAgentDialog}
          onClose={toggleQuizAgentDialog}
          quizId={quiz._id}
          shouldFetchQuiz={!isInternal}
        />
      ) : null}
    </Fragment>
  );
};

export default QuizMenu;
